/**
 * AiWinderEnrichPanel — the winder-specific AI enrichment UI.
 *
 * A lightweight mirror of AiElectronicModuleEnrichPanel: the `enrichWinder`
 * callable returns the same per-field envelope
 * (`{ slotCount: { value, confidence, reasoning }, … }`), so the presentation is
 * the same shape — an optional context textarea, a Run trigger, loading/error
 * states, and a read-only per-field preview of what the model proposed.
 *
 * One difference from the module panel: Run is gated on make + model rather than
 * on a saved doc id. `enrichWinder` takes the identity pair directly instead of
 * loading a document, so a brand-new winder can be enriched before its first
 * save.
 *
 * Presentation only — the caller owns the run wiring and the apply-review step.
 */
import type { WinderEnrichResult } from '../../services/firebase';
import {
  coerceWinderValue,
  formatWinderValue,
  getWinderConfidence,
  getWinderFieldLabel,
  getWinderReasoning,
  getWinderSuggestion,
  suggestedWinderKeys,
} from '../../services/winderEnrichFields';
import { Button } from '../common/Button';

/** Async state for the winder AI enrichment flow. */
export interface WinderAiEnrichState {
  loading?: boolean;
  result?: WinderEnrichResult;
  error?: string;
}

/** One row of the preview table. */
interface PreviewRow {
  key: string;
  label: string;
  value: string;
  confidence: string;
  /** '' when the row above already showed this same text — see `previewRows`. */
  reasoning: string;
}

/**
 * The model's proposals as rows, in registry order.
 *
 * Driven by `suggestedWinderKeys` rather than by `Object.entries(result)`, which
 * is what this panel used to walk. Iterating the RAW result meant the nine
 * security specs arrived as the one `safeSpecs` envelope the model returns: a
 * single row, labelled with the bare key because the registry has no entry for
 * it, showing "[object Object]" because the envelope is an object. The registry
 * flattens that envelope into nine labelled cells — reviewing them one at a time
 * is the point (see `WinderEnrichFieldSpec.safeSpecKey`), and it is what the
 * apply diff and the bulk grid have always shown.
 *
 * Values are COERCED before formatting, so what the panel previews is what an
 * apply would actually write, not the raw model output that may still be
 * discarded a step later.
 *
 * A key the model omitted, or answered unusably, never becomes a row —
 * `suggestedWinderKeys` drops it. That also replaces the old "render it and
 * filter on the string being non-empty" pass, which existed to keep a proposed
 * `false` (a real answer: "no lock") from being filtered out as falsy.
 */
function previewRows(result: WinderEnrichResult): PreviewRow[] {
  const seenReasoning = new Set<string>();
  return suggestedWinderKeys(result).map((key) => {
    const coerced = coerceWinderValue(key, getWinderSuggestion(result, key));
    // All nine security cells share the envelope's one reasoning paragraph.
    // Printing it under each of them would bury the values it explains, so it
    // shows once, against the first cell that carries it.
    const reasoning = getWinderReasoning(result, key);
    const isRepeat = reasoning !== '' && seenReasoning.has(reasoning);
    if (reasoning) seenReasoning.add(reasoning);
    return {
      key,
      label: getWinderFieldLabel(key),
      value: formatWinderValue(coerced),
      confidence: getWinderConfidence(result, key),
      reasoning: isRepeat ? '' : reasoning,
    };
  });
}

const confidenceClass = (c?: string): string => {
  switch (c) {
    case 'high':
      return 'bg-emerald-100 text-emerald-800';
    case 'medium':
      return 'bg-amber-100 text-amber-800';
    case 'low':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

interface AiWinderEnrichPanelProps {
  state: WinderAiEnrichState;
  /** Optional free-text hint passed to the callable. */
  context: string;
  onContextChange: (value: string) => void;
  /** True when make or model is still empty. */
  runDisabled: boolean;
  /** Tooltip explaining why Run is disabled (shown when `runDisabled`). */
  disabledReason?: string;
  onRun: () => void;
  /** Open the apply-review diff. */
  onAccept: () => void;
  onClose: () => void;
}

export function AiWinderEnrichPanel({
  state,
  context,
  onContextChange,
  runDisabled,
  disabledReason,
  onRun,
  onAccept,
  onClose,
}: AiWinderEnrichPanelProps) {
  const { loading, result, error } = state;
  const entries = result ? previewRows(result) : [];

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-purple-700 mb-1">
          Context (optional)
        </label>
        <textarea
          value={context}
          onChange={(e) => onContextChange(e.target.value)}
          rows={2}
          placeholder="Any grounding hint for the model — e.g. the product page URL, or the generation this winder belongs to…"
          className="w-full border border-purple-200 bg-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onRun}
          disabled={runDisabled || loading}
          title={runDisabled ? disabledReason : undefined}
        >
          {loading ? 'Enriching…' : result ? 'Re-run enrichment' : 'Run enrichment'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
        {runDisabled && disabledReason && (
          <span className="text-xs text-amber-700">{disabledReason}</span>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {result && !loading && (
        <div className="space-y-2">
          {entries.length === 0 ? (
            <div className="text-sm text-gray-500">
              No suggestions returned for this winder. The model is told to prefer
              nothing over a guess — an unrecognised make/model pair legitimately
              returns empty.
            </div>
          ) : (
            <div className="border border-purple-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto bg-white">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-100">
                  {entries.map((row) => (
                    <tr key={row.key} className="align-top">
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {row.label}
                      </td>
                      <td className="px-3 py-2 text-gray-900 break-words">
                        <div className="font-mono text-emerald-700">{row.value}</div>
                        {row.reasoning && (
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {row.reasoning}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {row.confidence && (
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-medium ${confidenceClass(
                              row.confidence,
                            )}`}
                          >
                            {row.confidence}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {entries.length > 0 && (
            <div className="flex justify-end">
              <Button variant="primary" size="sm" onClick={onAccept}>
                Review &amp; apply…
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
